/**
 * Date-intent engine for the 4 country gold hubs.
 * Pure Intl, no deps. Runs at SSR per request — midnight rollover is
 * automatic in each country's local timezone, no cron needed.
 */

export type CountryKey = "pakistan" | "india" | "uae" | "saudi";

export const TZ: Record<CountryKey, string> = {
  pakistan: "Asia/Karachi",
  india: "Asia/Kolkata",
  uae: "Asia/Dubai",
  saudi: "Asia/Riyadh",
};

const TZ_OFFSET_MIN: Record<CountryKey, number> = {
  pakistan: 5 * 60,
  india: 5 * 60 + 30,
  uae: 4 * 60,
  saudi: 3 * 60,
};

const MONTHS_EN = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const MONTHS_UR = [
  "جنوری","فروری","مارچ","اپریل","مئی","جون",
  "جولائی","اگست","ستمبر","اکتوبر","نومبر","دسمبر",
];
const MONTHS_AR = [
  "يناير","فبراير","مارس","أبريل","مايو","يونيو",
  "يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر",
];
const MONTHS_HI = [
  "जनवरी","फ़रवरी","मार्च","अप्रैल","मई","जून",
  "जुलाई","अगस्त","सितंबर","अक्टूबर","नवंबर","दिसंबर",
];
const WEEKDAY_EN = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

export type DateParts = {
  iso: string;
  isoWithTZ: string;
  slug: string;
  longEN: string;
  longUR: string;
  longAR: string;
  longHI: string;
  weekdayEN: string;
  monthEN: string;
  year: string;
  tomorrowISO: string;
  yesterdayISO: string;
  weekStartISO: string;
  yesterdayLongEN: string;
  tomorrowLongEN: string;
  weekStartLongEN: string;
  nowLabel: string;
  tzAbbr: string;
};

function shiftToLocal(now: Date, country: CountryKey): Date {
  // Build a Date representing the wall-clock time in the target TZ.
  const offsetMs = TZ_OFFSET_MIN[country] * 60_000;
  return new Date(now.getTime() + offsetMs);
}

function fmtLong(d: Date, months: readonly string[]): string {
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function fmtISO(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const TZ_ABBR: Record<CountryKey, string> = {
  pakistan: "PKT",
  india: "IST",
  uae: "GST",
  saudi: "AST",
};

export function getDateParts(country: CountryKey, now: Date = new Date()): DateParts {
  const local = shiftToLocal(now, country);
  const tomorrow = new Date(local.getTime() + 86_400_000);
  const yesterday = new Date(local.getTime() - 86_400_000);
  // Week start = previous Monday (or today if Monday).
  const dow = local.getUTCDay(); // 0=Sun..6=Sat
  const daysSinceMon = (dow + 6) % 7;
  const weekStart = new Date(local.getTime() - daysSinceMon * 86_400_000);

  const longEN = fmtLong(local, MONTHS_EN);
  const slug = longEN.toLowerCase().replace(/\s+/g, "-");
  const iso = fmtISO(local);

  // Wall-clock hh:mm in TZ via shift.
  const hh = String(local.getUTCHours()).padStart(2, "0");
  const mm = String(local.getUTCMinutes()).padStart(2, "0");
  const tzAbbr = TZ_ABBR[country];

  return {
    iso,
    isoWithTZ: `${iso}T${hh}:${mm}:00`,
    slug,
    longEN,
    longUR: fmtLong(local, MONTHS_UR),
    longAR: fmtLong(local, MONTHS_AR),
    longHI: fmtLong(local, MONTHS_HI),
    weekdayEN: WEEKDAY_EN[local.getUTCDay()],
    monthEN: MONTHS_EN[local.getUTCMonth()],
    year: String(local.getUTCFullYear()),
    tomorrowISO: fmtISO(tomorrow),
    yesterdayISO: fmtISO(yesterday),
    weekStartISO: fmtISO(weekStart),
    yesterdayLongEN: fmtLong(yesterday, MONTHS_EN),
    tomorrowLongEN: fmtLong(tomorrow, MONTHS_EN),
    weekStartLongEN: fmtLong(weekStart, MONTHS_EN),
    nowLabel: `${longEN}, ${hh}:${mm} ${tzAbbr}`,
    tzAbbr,
  };
}

/**
 * Append current month + year to a page title for Google Discover
 * freshness intent. Defaults to Pakistan TZ so PK bill/tool pages
 * roll over at Karachi midnight. Skips gracefully if the title
 * already contains the current year.
 */
export function withTodayDate(
  title: string,
  country: CountryKey = "pakistan",
  now: Date = new Date(),
): string {
  const dp = getDateParts(country, now);
  if (title.includes(dp.year)) return title;
  const stamp = `${dp.monthEN} ${dp.year}`;
  const withStamp = `${title} — Updated ${stamp}`;
  return withStamp.length <= 72 ? withStamp : title;
}